import h from 'hyperscript';

import type { SelectOption } from 'ui/select/select';
import { Select } from 'ui/select/select';

import type { TableRowDto } from 'cadenza/ui/base-table/model/table-row-dto';
import type { TableColumnsDto } from 'cadenza/ui/base-table/model/table-columns-dto';
import { hashCode } from 'cadenza/utils/hash-code';
import { logger } from 'cadenza/utils/logging';
import { getTextFromVisibleValues } from 'cadenza/conditions/table-condition/table-condition';
import { AnimationVisualization } from 'cadenza/conditions/animation/animation-visualization';
import type { WorkbookTableConditionApi } from 'cadenza/api-client/workbook-conditions/workbook-table-condition-api';

import i18n from './table-slider-animation-preview.properties';
import './table-slider-animation-preview.css';

const COMPONENT_NAME = 'd-table-slider-animation-preview';
const DEFAULT_LIMIT = 30;

interface RowResult {
  id: string;
  text: string;
  value: string[];
}

interface SelectValue extends RowResult {
  selected: boolean;
}

interface Results {
  results: RowResult[];
  pagination: { more: boolean };
}

interface DataValue {
  id: string;
  value: string;
}

export class TableSliderAnimationPreview extends HTMLElement {

  _tableColumnsDef: TableColumnsDto;

  _startSelect: Select;

  _endSelect: Select;

  _frameInfoLabel: HTMLLabelElement;

  _maxFrames: number;

  _conditionApi: WorkbookTableConditionApi;

  /** A cache of all the possible values that the selects can have in order to use for indexes validation. */
  _allValues: DataValue[] = [];

  _animationVisualization: AnimationVisualization;

  _startIndex = -1;

  _endIndex = -1;

  _numberOfFrames = 0;

  _startAfterEndErrorMessage = h('d-alert.d-alert-small', {
    type: 'error',
    message: i18n('table.slider.animation.preview.start.after.end')
  });

  _dataLoadingPromise: Promise<void>;

  constructor (
    selectedStart: SelectOption,
    rangeEnd: SelectOption,
    maxFrames: number,
    conditionApi: WorkbookTableConditionApi,
    tableColumnsDef: TableColumnsDto) {
    super();
    this._maxFrames = maxFrames;
    this._conditionApi = conditionApi;
    this._tableColumnsDef = tableColumnsDef;
    this._startSelect = this._buildSelector(selectedStart);
    this._endSelect = this._buildSelector(rangeEnd);
    this._frameInfoLabel = h('label', '');
    this._animationVisualization = new AnimationVisualization({
      numberOfFrames: 0,
      frameLabels: ''
    });
    this._dataLoadingPromise = this._readAllValues();
  }

  get startIndex () {
    return this._startIndex;
  }

  get endIndex () {
    return this._endIndex;
  }

  get dataLoadingPromise () {
    return this._dataLoadingPromise;
  }

  _buildSelector (selectedValue: SelectOption): Select {
    let select = new Select({
      query: this._getQuery(),
      multiple: false,
      removeOnly: false
    });
    select.accessibleSelect.required = true;
    select.addEventListener('change', () => this._handleSelectionChanged());
    select.selection = selectedValue;
    return select;
  }

  _handleSelectionChanged () {
    const startValue = this._startSelect.selection[0] as SelectValue;
    const endValue = this._endSelect.selection[0] as SelectValue;
    if (startValue && endValue) {
      this._startIndex = this._allValues.map(value => value.id).indexOf(startValue.id);
      this._endIndex = this._allValues.map(value => value.id).indexOf(endValue.id);
      if (this._startIndex >= this._endIndex) {
        this._startSelect.accessibleSelect.setCustomValidity(' ');
        this._endSelect.accessibleSelect.setCustomValidity(' ');
        this._animationVisualization.updateFrameLabels([], 0, '');
        this.append(this._startAfterEndErrorMessage);
        this._numberOfFrames = 0;
      } else {
        this._startAfterEndErrorMessage.remove();
        this._startSelect.accessibleSelect.setCustomValidity('');
        this._endSelect.accessibleSelect.setCustomValidity('');
        const selectedNumberOfFrames = this._endIndex - this._startIndex + 1;
        this._numberOfFrames = Math.min(selectedNumberOfFrames, this._maxFrames);
        this._animationVisualization.updateFrameLabels(
          this._allValues.slice(this._startIndex, this._startIndex + this._numberOfFrames)
            .map(item => item.value).map(value => value.lastIndexOf(',') >= 0 ? value.substring(0, value.lastIndexOf(',')) : value),
          this._numberOfFrames,
          selectedNumberOfFrames > this._maxFrames ? `${selectedNumberOfFrames - this._numberOfFrames} ${i18n('table.slider.animation.preview.frames')}` : '');
      }
      this._updateFrameInfoLabel();
    }
  }

  /**
   * The function used to format a row for display in select2s results
   *
   * @return a function, which takes a row as parameter and returns the text * for display by select2
   */
  _createRowConverter () {
    return (row: TableRowDto) => getTextFromVisibleValues(
      row.formatted,
      this._tableColumnsDef.columns
    );
  }

  _getQuery () {
    const limit = DEFAULT_LIMIT;
    return ({ term, page = 1 }: { term: string; page: number }, callback: (args: Results) => void) => {
      const offset = (page - 1) * limit;
      const options = this._getOptions(offset, limit, term, true);
      this._conditionApi.getConditionTable(options).then(tableData => {
        const rows = tableData.rows;
        callback({
          results: rows.map(row => ({
            id: this._buildId(row),
            text: this._createRowConverter()(row),
            value: row.values as string[]
          })),
          pagination: {
            more: (offset + limit < tableData.rowsTotal)
          }
        });
      }).catch(err => {
        if (err.name !== 'AbortError') {
          logger.error('Failed to load condition table', err);
        }
      });
    };
  }

  _readAllValues () {
    const options = this._getOptions(0, -1, '', false);
    return this._conditionApi.getConditionTable(options).then(tableData => {
      this._allValues.push(...(tableData.rows).map(row => {
        const dataValue: DataValue = ({ id: this._buildId(row), value: row.values.join() });
        return dataValue;
      }));
      this._handleSelectionChanged();
    }).catch(err => {
      if (err.name !== 'AbortError') {
        logger.error('Failed to load condition table data', err);
      }
    });
  }

  _updateFrameInfoLabel () {
    this._frameInfoLabel.textContent = i18n('table.slider.animation.preview.number.of.frames', {
      actualFrames: this._numberOfFrames,
      frameLimit: this._maxFrames
    });
  }

  _buildId (row: TableRowDto) {
    return String(hashCode(JSON.stringify(row.values)));
  }

  _getOptions (offset: number, limit: number, filterText: string, noCount: boolean) {
    return { offset, limit, filterText, noCount };
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this._render();
  }

  _render () {
    this.innerHTML = '';
    this.append(
      h('.d-table-slider-animation-preview--range-settings-panel', [
        h('form-group', [
          h('label', i18n('table.slider.animation.preview.start')),
          this._startSelect
        ]),
        this._frameInfoLabel,
        h('form-group', [
          h('label', i18n('table.slider.animation.preview.end')),
          this._endSelect
        ]) ]),
      this._animationVisualization);
  }

}

customElements.define(COMPONENT_NAME, TableSliderAnimationPreview);
